import React, { useState, useEffect } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Edit, Trash2, Plus, User, Users, Filter, Archive, RefreshCw, FileText, History, Copy, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Search, Mail, Building2, Loader2, ArrowUpDown, ArrowUp, ArrowDown, FileDown, Settings, Shield, Warehouse } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { User as UserType } from "../types/index";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Pagination, PaginationContent, PaginationItem } from "../components/ui/pagination";
import { getDepartmentCode, getDepartmentByCode, toSydneyDate } from "../utils/helpers";
import api from "../utils/api";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

import { TrainingQualificationRegister } from "../components/TrainingQualificationRegister";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/casl/AbilityContext";
import AccessRestricted from "../components/AccessRestricted";

export default function PeoplePage() {
  const { departments, users, fetchManagerUsers, managerUsers, deleteUser, unarchiveUser, jobRoles, procedures, activities } = useData();
  const [departmentsDropdown, setDepartmentsDropdown] = useState<any[]>([]);
  const { currentUser } = useAuth();
  const ability = useAbility(AbilityContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [timeFilter, setTimeFilter] = useState<string>("all");
  const [subcontractorFilter, setSubcontractorFilter] = useState<string>("all");
  const [sortByTime, setSortByTime] = useState<string>("none");
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' | 'none' }>({ key: 'name', direction: 'asc' });
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [archiveId, setArchiveId] = useState("");
  const [deleteBlockers, setDeleteBlockers] = useState<any[]>([]);
  const [deleteChecksLoading, setDeleteChecksLoading] = useState(false);

  const safeRender = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') {
      // If it's an object, try to extract meaningful properties
      if (value.name) return String(value.name);
      if (value.title) return String(value.title);
      if (value.departmentName) return String(value.departmentName);
      if (value.departmentCode) return String(value.departmentCode);
      return 'Unknown';
    }
    return String(value);
  };

  useEffect(() => {
    if (managerUsers.length === 0) fetchManagerUsers();
  }, []);

  // Deleting is blocked while the user manages a Service Desk or an open Yard
  useEffect(() => {
    const fetchDeleteChecks = async () => {
      if (!deleteId || !openDelete) return;
      try {
        setDeleteChecksLoading(true);
        const data = await api.get(`/api/users/${deleteId}/archive-checks`);
        if (data && data.success) {
          setDeleteBlockers((data.blockers || []).filter((b: any) => b.type === 'service_desk_admin' || b.type === 'yard_manager'));
        }
      } catch (error) {
        console.error('Error fetching delete checks:', error);
      } finally {
        setDeleteChecksLoading(false);
      }
    };
    fetchDeleteChecks();
  }, [deleteId, openDelete]);



  // Only allow access if user has permission to read person page or person i manage page
  if (!ability.can(ACTIONS.READ, RESOURCES.PERSON_PAGE) && !ability.can(ACTIONS.READ, RESOURCES.PERSON_I_MANAGE_PAGE)) {
    return <AccessRestricted />;
  }

  // Get only the actual job roles from the database
  const availableJobRoles = React.useMemo(() => {
    const uniqueJobRoles = new Map();

    jobRoles.forEach(role => {
      if (role.archived) return;

      const normalizedTitle = role.title.trim().toLowerCase();

      if (!uniqueJobRoles.has(normalizedTitle)) {
        uniqueJobRoles.set(normalizedTitle, {
          id: role.id,
          title: role.title,
          departmentId: role.departmentId
        });
      }
    });

    const uniqueRolesArray = Array.from(uniqueJobRoles.values());
    return uniqueRolesArray.sort((a, b) => a.title.localeCompare(b.title));
  }, [jobRoles]);

  const getDaysWithCompany = (startDateString?: string) => {
    if (!startDateString) return -1;
    const startDate = new Date(startDateString);
    const now = new Date();
    return Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const calculateTimeWithCompany = (startDateString?: string) => {
    if (!startDateString) return "N/A";
    const startDate = new Date(startDateString);
    const now = new Date();

    let years = now.getFullYear() - startDate.getFullYear();
    let months = now.getMonth() - startDate.getMonth();

    if (months < 0 || (months === 0 && now.getDate() < startDate.getDate())) {
      years--;
      months += 12;
    }

    if (now.getDate() < startDate.getDate() && months > 0) {
      months--;
    }

    if (years > 0) {
      if (months > 0) return `${years}y ${months}m`;
      return `${years}y`;
    }
    if (months > 0) return `${months}m`;

    const days = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return days >= 0 ? `${days}d` : "Not started";
  };

  const [searchTerm, setSearchTerm] = useState("");

  // Filter users based on selected department and archived status
  const filteredUsers = users.filter(user => {
    // First filter by archived status
    const isArchived = user.archived === true;
    if (showArchived !== isArchived) {
      return false;
    }

    // Filter by department if not "all"
    if (departmentFilter !== "all") {
      const selectedDept = departments.find(d => d.id.toString() === departmentFilter);
      if (!selectedDept || !user.department) {
        return false;
      }
      
      const userDeptId = typeof user.department === 'object' ? user.department.id : user.department;
      const userDeptCode = typeof user.department === 'object' ? user.department.departmentCode : user.department;

      if (userDeptId != selectedDept.id && userDeptCode != selectedDept.departmentCode) {
        return false;
      }
    }

    // Filter by time with company
    if (timeFilter !== "all") {
      const days = getDaysWithCompany(user.startDate);
      if (days === -1) return false;
      const years = days / 365.25;
      if (timeFilter === "<1" && years >= 1) return false;
      if (timeFilter === "1-3" && (years < 1 || years >= 3)) return false;
      if (timeFilter === "3-5" && (years < 3 || years >= 5)) return false;
      if (timeFilter === "5+" && years < 5) return false;
    }

    // Filter by subcontractor flag. Empty means the value has not been recorded.
    if (subcontractorFilter === "yes" && user.isSubcontractor !== true) return false;
    if (subcontractorFilter === "no" && user.isSubcontractor !== false) return false;
    if (
      subcontractorFilter === "empty" &&
      user.isSubcontractor !== null &&
      user.isSubcontractor !== undefined
    ) {
      return false;
    }

    return true;
  });

  // Add search filter
  const filteredUsersWithSearch = filteredUsers.filter(user => {
    if (searchTerm && !user.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  const getDepartmentName = (deptIdentifier: any) => {
    if (!deptIdentifier) return "N/A";
    
    // Handle object if passed
    if (typeof deptIdentifier === 'object') {
      if (deptIdentifier.departmentName) return safeRender(deptIdentifier.departmentName);
      if (deptIdentifier.departmentCode) deptIdentifier = deptIdentifier.departmentCode;
      else if (deptIdentifier.id) deptIdentifier = deptIdentifier.id;
      else return "Unknown";
    }

    const deptIdStr = deptIdentifier.toString();
    const department = departments.find(d => 
      d.id.toString() === deptIdStr || 
      d.departmentCode === deptIdStr
    );
    return department ? safeRender(department.departmentName) : safeRender(deptIdentifier);
  };

  const getManagerName = (managerId?: string | null | UserType) => {
    if (!managerId) return "None";
    if (typeof managerId === 'object' && managerId.name) return managerId.name;
    if (typeof managerId === 'object' && managerId.id) managerId = managerId.id;
    const manager = users.find(u => u.id == managerId) || managerUsers.find(u => u.id == managerId);
    return manager ? manager.name : "Unknown";
  };

  const sortedAndFilteredUsers = [...filteredUsersWithSearch].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (direction === 'none') return 0;

    let valA: any = "";
    let valB: any = "";

    switch (key) {
      case 'name':
        valA = a.name.toLowerCase();
        valB = b.name.toLowerCase();
        break;
      case 'email':
        valA = a.email.toLowerCase();
        valB = b.email.toLowerCase();
        break;
      case 'department':
        valA = getDepartmentName(typeof a.department === 'object' ? a.department.id : a.department).toLowerCase();
        valB = getDepartmentName(typeof b.department === 'object' ? b.department.id : b.department).toLowerCase();
        break;
      case 'jobRole':
        valA = a.jobRole.toLowerCase();
        valB = b.jobRole.toLowerCase();
        break;
      case 'userRole':
        valA = a.userRole.toLowerCase();
        valB = b.userRole.toLowerCase();
        break;
      case 'startDate':
        valA = a.startDate ? new Date(a.startDate).getTime() : 0;
        valB = b.startDate ? new Date(b.startDate).getTime() : 0;
        break;
      case 'timeWithCompany':
        valA = getDaysWithCompany(a.startDate);
        valB = getDaysWithCompany(b.startDate);
        break;
      case 'manager':
        valA = getManagerName(a.manager).toLowerCase();
        valB = getManagerName(b.manager).toLowerCase();
        break;
      default:
        return 0;
    }

    if (valA < valB) return direction === 'asc' ? -1 : 1;
    if (valA > valB) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Calculate pagination values
  const totalPages = Math.ceil(sortedAndFilteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = sortedAndFilteredUsers.slice(startIndex, endIndex);

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [departmentFilter, timeFilter, subcontractorFilter, sortByTime, searchTerm, showArchived]);

  // Helper function to get user name
  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : "Unknown";
  };

  // Helper function to format archive date
  const formatArchiveDate = (dateString: string | undefined | null) => {
    if (!dateString) return "Unknown";
    return toSydneyDate(dateString);
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await deleteUser(deleteId);
      await fetchManagerUsers();
      setDeleteId("");
      setOpenDelete(false);
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnarchive = async () => {
    if (!currentUser) return;
    try {
      setIsLoading(true);
      await unarchiveUser(archiveId, currentUser.id);
      await fetchManagerUsers();
      setArchiveId("");
      setOpenUnarchive(false);
    } catch (error) {
      console.error("Error unarchiving user:", error);
    } finally {
      setIsLoading(false);
    }
  };



  // Helper functions to count user associations
  const getManagedPeopleCount = (userId: string) => {
    return users.filter(u => u.manager === userId && !u.archived).length;
  };

  const getOwnedProceduresCount = (userId: string) => {
    return procedures.filter(p => p.procedureOwner === userId && !p.archived).length;
  };

  const getResponsibleActivitiesCount = (userId: string) => {
    return activities.filter(a => a.responsiblePerson === userId).length;
  };

  const getAccountableActivitiesCount = (userId: string) => {
    return activities.filter(a => a.accountablePerson === userId).length;
  };

  const hasHistoricallyOwnedProcedures = (userId: string) => {
    // Check if user has ever owned any procedures (including archived ones and deleted ones)
    return procedures.some(p =>
      // Check current ownership
      (typeof p.procedureOwner === 'string' ? p.procedureOwner === userId : p.procedureOwner?.id === userId) ||
      // Check if they created the procedure
      (typeof p.createdBy === 'string' ? p.createdBy === userId : p.createdBy?.id === userId) ||
      // Check if they modified the procedure
      (typeof p.lastModifiedBy === 'string' ? p.lastModifiedBy === userId : p.lastModifiedBy?.id === userId) ||
      // Check activity log for past ownership
      p.activityLogs?.some(log => {
        const userName = users.find(u => u.id === userId)?.name;
        return log.changeDescription?.includes(`Procedure owner changed from ${userName}`);
      })
    );
  };

  const hasHistoricalActivities = (userId: string) => {
    // Check if user has ever been responsible, accountable, consulted, or informed in any activity
    return activities.some(a =>
      a.responsiblePerson === userId ||
      a.accountablePerson === userId ||
      a.consultedPerson === userId ||
      a.informedPerson === userId
    );
  };

  const canDeleteUser = (userId: string) => {
    // According to the rules:
    // - Can't delete if user manages people (active)
    // - Can't delete if user currently owns procedures
    // - Can't delete if user has historical procedures
    // - Can't delete if user has current or historical activities

    if (getManagedPeopleCount(userId) > 0) return false; // No active managed people
    if (getOwnedProceduresCount(userId) > 0) return false; // No current procedures
    if (hasHistoricallyOwnedProcedures(userId) || hasHistoricalActivities(userId)) return false; // No historical data

    return true;
  };

  // Update handlers to reset page
  const handleDepartmentFilter = (value: string) => {
    setDepartmentFilter(value);
    setCurrentPage(1);
  };

  const handleTimeFilter = (value: string) => {
    setTimeFilter(value);
    setCurrentPage(1);
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' | 'none' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'none';
    }
    
    setSortConfig({ key, direction });
    
    // Sync with sortByTime dropdown if key is 'timeWithCompany'
    if (key === 'timeWithCompany') {
      setSortByTime(direction === 'none' ? 'none' : direction);
    } else {
      setSortByTime('none');
    }
  };

  const handleSortByTime = (value: string) => {
    setSortByTime(value);
    setSortConfig({ key: 'timeWithCompany', direction: value as 'asc' | 'desc' | 'none' });
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleShowArchived = (value: string) => {
    setShowArchived(value === "archived");
    setCurrentPage(1);
  };

  const [activeMainTab, setActiveMainTab] = useState("directory");

  const renderSortableHeader = (label: string, key: string) => {
    const isActive = sortConfig.key === key;
    const direction = sortConfig.direction;

    return (
      <TableHead 
        className="cursor-pointer hover:bg-slate-50 transition-colors py-3 group font-semibold text-slate-900" 
        onClick={() => handleSort(key)}
      >
        <div className="flex items-center gap-1 whitespace-nowrap">
          {label}
          <div className="flex items-center">
            {isActive && direction !== 'none' ? (
              direction === 'asc' ? <ArrowUp size={14} className="text-slate-900" /> : <ArrowDown size={14} className="text-slate-900" />
            ) : (
              <ArrowUpDown size={14} className="opacity-0 group-hover:opacity-30 transition-opacity" />
            )}
          </div>
        </div>
      </TableHead>
    );
  };

  const handleExportCSV = () => {
    const headers = ["Name", "Email", "Mobile", "Job Role", "User Role", "Department", "Manager", "Start Date", "Time with Company", "Subcontractor", "Subcontractor Vendor", "Status"];
    
    const escapeCSV = (val: any) => {
      if (val === null || val === undefined) return "";
      const str = String(val);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = sortedAndFilteredUsers.map(user => [
      escapeCSV(user.name),
      escapeCSV(user.email),
      escapeCSV(user.mobile),
      escapeCSV(user.jobRole),
      escapeCSV(user.userRole),
      escapeCSV(getDepartmentName(typeof user.department === 'object' ? user.department.id : user.department)),
      escapeCSV(getManagerName(user.manager)),
      escapeCSV(user.startDate ? toSydneyDate(user.startDate) : ""),
      escapeCSV(calculateTimeWithCompany(user.startDate)),
      escapeCSV(
        user.isSubcontractor === true ? "Yes" : user.isSubcontractor === false ? "No" : "Empty"
      ),
      escapeCSV(
        user.subcontractorVendor?.tradingName || user.subcontractorVendor?.legalName || ""
      ),
      escapeCSV(user.archived ? "Archived" : "Active")
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `idarah-people-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4 md:space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold">People</h1>
        <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="flex items-center gap-2 w-full md:w-auto border-slate-200 hover:bg-slate-50"
          >
            <FileDown size={16} />
            <span>Export CSV</span>
          </Button>
          <Can I={ACTIONS.READ} a={RESOURCES.PERSON_SETTINGS}>
            <Button
              variant="outline"
              onClick={() => navigate("/people/settings")}
              className="flex items-center gap-2 w-full md:w-auto border-slate-200 hover:bg-slate-50"
            >
              <Settings size={16} />
              <span>Settings</span>
            </Button>
          </Can>
          {activeMainTab === "directory" && !showArchived && (
            <Can I={ACTIONS.CREATE} a={RESOURCES.PERSON}>
              <Link to="/people/add">
                <Button className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0">
                  <Plus size={16} />
                  <span>Add Person</span>
                </Button>
              </Link>
            </Can>
          )}
      </div>
    </div>

      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 inline-flex h-10 items-center justify-center rounded-full text-slate-500 mb-6">
          <TabsTrigger
            value="directory"
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all"
          >
            Directory
          </TabsTrigger>
          <TabsTrigger
            value="qualifications"
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all"
          >
            Training & Qualification Register
          </TabsTrigger>
        </TabsList>

        <TabsContent value="directory" className="space-y-6 mt-0">
          {/* Tabs and Filters */}
          <div className="flex flex-col gap-4">
        <Tabs defaultValue="active" className="w-full" onValueChange={handleShowArchived}>
          <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
            <TabsTrigger
              value="active"
              className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white"
            >
              Active
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white"
            >
              Archived
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 relative flex-wrap">
          <div className="relative w-full md:flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search people..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 w-full"
            />
          </div>

          <div className="w-full md:w-[250px] min-w-0">
            <Select value={departmentFilter} onValueChange={handleDepartmentFilter}>
              <SelectTrigger className="w-full bg-white truncate overflow-hidden">
                <div className="truncate w-full text-left">
                  <SelectValue placeholder="Filter by Department" className="truncate" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments
                  .filter(dept => !dept.deleted)
                  .map((dept) => (
                    <SelectItem key={dept.id} value={dept.id.toString()}>
                      {safeRender(dept.departmentCode)} - {safeRender(dept.departmentName)}{dept.archived ? " (Archived)" : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col sm:flex-row items-center w-full md:w-auto gap-2">
            <Select value={timeFilter} onValueChange={handleTimeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Time with Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Time</SelectItem>
                <SelectItem value="<1">&lt; 1 year</SelectItem>
                <SelectItem value="1-3">1-3 years</SelectItem>
                <SelectItem value="3-5">3-5 years</SelectItem>
                <SelectItem value="5+">5+ years</SelectItem>
              </SelectContent>
            </Select>

            <Select value={subcontractorFilter} onValueChange={setSubcontractorFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Subcontractor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subcontractors</SelectItem>
                <SelectItem value="yes">Subcontractor: Yes</SelectItem>
                <SelectItem value="no">Subcontractor: No</SelectItem>
                <SelectItem value="empty">Subcontractor: Empty</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortByTime} onValueChange={handleSortByTime}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Sort by Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Default Sort</SelectItem>
                <SelectItem value="asc">Time: Lowest to Highest</SelectItem>
                <SelectItem value="desc">Time: Highest to Lowest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {/* <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {showArchived ? "Archived People" : "Active People"}
            {departmentFilter !== "all" && ` - ${getDepartmentName(departmentFilter)}`}
          </CardTitle>
        </CardHeader>
        <CardContent> */}
      {/* Mobile View */}
      <div className="md:hidden space-y-3">
        {currentUsers.length > 0 ? currentUsers.map((user) => (
          <Card key={user.id} className="p-4">
            <div className="flex flex-col space-y-3">
              {/* Top part: Details and Actions */}
              <div className="flex justify-between items-start gap-4">
                {/* Left Column: User Details */}
                <div className="flex flex-col gap-1 flex-grow min-w-0">
                  <Link to={`/people/${user.id}`} className="font-semibold text-base text-primary hover:underline leading-tight">
                    {user.name}
                  </Link>
                  <span className="text-sm text-muted-foreground">{user.jobRole}</span>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <Mail size={14} />
                    <span className="truncate">{user.email}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 size={14} />
                    <span>
                      {(() => {
                        if (user.isTemporary && user.temporaryDepartment) return user.temporaryDepartment;
                        const dept = departments.find(d => 
                          (typeof user.department === 'string' && d.departmentCode === user.department) ||
                          (typeof user.department === 'object' && d.id === user.department?.id)
                        );
                        if (!dept) return safeRender(user.department) || user.temporaryDepartment || "None";
                        return (
                          <Link to={`/departments/${dept.id}`} className="hover:underline text-primary truncate">
                            {safeRender(dept.departmentCode)} - {safeRender(dept.departmentName)}
                          </Link>
                        );
                      })()}
                    </span>
                  </div>

                  <div className="flex gap-4 mt-2">
                    <div className="text-sm">
                      <span className="text-muted-foreground block text-xs">Start Date</span>
                      <p className="text-xs text-muted-foreground whitespace-nowrap">
                        {user.startDate ? toSydneyDate(user.startDate) : "N/A"}
                      </p>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground block text-xs">Time with Company</span>
                      <span className="font-medium">{calculateTimeWithCompany(user.startDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Action Buttons */}
                <div className="flex items-center">
                  {!showArchived && !user.archived && (
                    <>
                      <Can I={ACTIONS.UPDATE} a={RESOURCES.PERSON}>
                        <Link to={`/people/edit/${user.id}`} state={{ from: location.pathname }}>
                          <Button variant="ghost" size="icon">
                            <Edit size={18} />
                          </Button>
                        </Link>
                      </Can>

                      <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PERSON}>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Archive Person"
                          onClick={() => navigate(`/people/${user.id}/archive`)}
                        >
                          <Archive size={18} />
                        </Button>
                      </Can>

                      <Can I={ACTIONS.DELETE} a={RESOURCES.PERSON}>
                        <Dialog open={openDelete && deleteId === user.id} onOpenChange={(open) => { setOpenDelete(open); if (open) setDeleteId(user.id); }}>
                          <DialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 size={18} /></Button></DialogTrigger>
                          <DialogContent>
                            {/* Delete dialog content goes here */}
                          </DialogContent>
                        </Dialog>
                      </Can>
                    </>
                  )}
                  {showArchived && (
                    <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PERSON}>
                      <Dialog open={openUnarchive && archiveId === user.id} onOpenChange={(open) => { setOpenUnarchive(open); if (open) setArchiveId(user.id); }}>
                        <DialogTrigger asChild><Button variant="ghost" size="icon"><RefreshCw size={18} /></Button></DialogTrigger>
                        <DialogContent>
                          {/* Unarchive dialog content goes here */}
                        </DialogContent>
                      </Dialog>
                    </Can>
                  )}
                </div>

              </div>

              {/* Divider */}
              <hr />

              {/* Bottom part: Manager Info */}
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users size={16} />
                <span className="text-sm font-medium">Manager: {user.userManager ? user.userManager.name : getManagerName(user.manager)}</span>
              </div>
            </div>
          </Card>
        )) : (
          <div className="text-center text-muted-foreground py-8">No people found</div>
        )}
      </div>

      {/* Desktop View */}
      <Card className="hidden md:block">
        <CardContent className="p-3">
          <Table>
            <TableHeader>
              <TableRow>
                {renderSortableHeader("Name", "name")}
                {renderSortableHeader("Email", "email")}
                {renderSortableHeader("Department", "department")}
                {renderSortableHeader("Job Role", "jobRole")}
                {renderSortableHeader("User Role", "userRole")}
                {renderSortableHeader("Start Date", "startDate")}
                {renderSortableHeader("Time with Company", "timeWithCompany")}
                {renderSortableHeader("Manager", "manager")}
                <TableHead className="text-right font-semibold text-slate-900">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentUsers.length > 0 ? currentUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Link to={`/people/${user.id}`}>
                      <div className="flex items-center gap-2 hover:underline">
                        <User size={16} />
                        {user.name}
                        {user.archived && (
                          <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-800">
                            <Archive size={12} />
                            Archived
                          </Badge>
                        )}
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {user.email}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => {
                          navigator.clipboard.writeText(user.email);
                          toast.success("Email copied to clipboard");
                        }}
                      >
                        <Copy size={14} className="text-muted-foreground hover:text-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      if (user.isTemporary && user.temporaryDepartment) return user.temporaryDepartment;
                      const dept = departments.find(d => {
                        if (!user.department) return false;
                        if (typeof user.department === 'object') return d.id == user.department.id;
                        return d.id == user.department || d.departmentCode == user.department;
                      });
                      if (!dept) return safeRender(user.department) || user.temporaryDepartment || "None";
                      return (
                        <Link to={`/departments/${dept.id}`} className="hover:underline text-primary">
                          {safeRender(dept.departmentCode)} - {safeRender(dept.departmentName)}
                        </Link>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const jobRole = jobRoles.find(role => role.title === user.jobRole);
                      if (!jobRole) return user.jobRole;
                      return (
                        <Link to={`/job-roles/${jobRole.id}`} className="hover:underline text-primary">
                          {user.jobRole}
                        </Link>
                      );
                    })()}
                  </TableCell>
                  <TableCell>{user.userRole}</TableCell>
                  <TableCell>{user.startDate ? toSydneyDate(user.startDate) : "N/A"}</TableCell>
                  <TableCell>{calculateTimeWithCompany(user.startDate)}</TableCell>
                  <TableCell>
                    {user.manager ? (
                      <Link to={`/people/${user.userManager ? user.userManager.id : user.manager}`}>
                        <div className="flex items-center gap-2 hover:underline">
                          <Users size={16} />
                          {user.userManager ? user.userManager.name : getManagerName(user.manager)}
                        </div>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {!showArchived && !user.archived && (
                        <>
                          <Can I={ACTIONS.UPDATE} a={RESOURCES.PERSON}>
                            <Link to={`/people/edit/${user.id}`} state={{ from: location.pathname }}>
                              <Button variant="outline" size="icon">
                                <Edit size={16} />
                              </Button>
                            </Link>
                          </Can>

                          <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PERSON}>
                            <Button
                              variant="outline"
                              size="icon"
                              title="Archive Person"
                              onClick={() => navigate(`/people/${user.id}/archive`)}
                            >
                              <Archive size={16} />
                            </Button>
                          </Can>

                          <Can I={ACTIONS.DELETE} a={RESOURCES.PERSON}>
                            <Dialog open={openDelete && deleteId === user.id} onOpenChange={(open) => { setOpenDelete(open); if (open) setDeleteId(user.id); }}>
                              <DialogTrigger asChild><Button variant="outline" size="icon" className="text-destructive"><Trash2 size={16} /></Button></DialogTrigger>
                              <DialogContent>
                                <DialogHeader><DialogTitle className="text-lg font-medium">Delete Person</DialogTitle></DialogHeader>
                                {deleteChecksLoading ? (
                                  <div className="py-4 flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 size={16} className="animate-spin" />
                                    <span>Checking active associations...</span>
                                  </div>
                                ) : !canDeleteUser(user.id) || deleteBlockers.length > 0 ? (
                                  <div className="space-y-4">
                                    <div className="text-sm text-destructive font-medium">This person cannot be deleted due to the following:</div>
                                    <div className="space-y-2 text-sm">
                                      {getManagedPeopleCount(user.id) > 0 && (<div className="flex items-center gap-2"><Users size={16} className="text-muted-foreground" /><span>Manages {getManagedPeopleCount(user.id)} {getManagedPeopleCount(user.id) === 1 ? 'person' : 'people'}</span></div>)}
                                      {getOwnedProceduresCount(user.id) > 0 && (<div className="flex items-center gap-2"><FileText size={16} className="text-muted-foreground" /><span>Currently owns {getOwnedProceduresCount(user.id)} {getOwnedProceduresCount(user.id) === 1 ? 'procedure' : 'procedures'}</span></div>)}
                                      {hasHistoricallyOwnedProcedures(user.id) && (<div className="flex items-center gap-2"><FileText size={16} className="text-muted-foreground" /><span>Has previously owned procedures</span></div>)}
                                      {(hasHistoricalActivities(user.id)) && (<div className="flex items-center gap-2"><History size={16} className="text-muted-foreground" /><span>Has historical data linked to activities</span></div>)}
                                      {deleteBlockers.map((blocker, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                          {blocker.type === 'service_desk_admin' && <Shield size={16} className="text-muted-foreground" />}
                                          {blocker.type === 'yard_manager' && <Warehouse size={16} className="text-muted-foreground" />}
                                          <span>{blocker.label}</span>
                                        </div>
                                      ))}
                                    </div><p className="text-sm text-muted-foreground mt-4">You can archive them to keep a historical record while making them inactive in the system.</p>
                                  </div>
                                ) : (<div className="py-4"><p>Are you sure you want to delete the person "{user.name}"?</p><p className="text-sm text-muted-foreground mt-2">This action cannot be undone.</p></div>)}
                                <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button variant="destructive" onClick={handleDelete} disabled={deleteChecksLoading || !canDeleteUser(user.id) || deleteBlockers.length > 0}>Delete</Button></DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </Can>
                        </>
                      )}
                      {showArchived && (
                        <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PERSON}>
                          <Dialog open={openUnarchive && archiveId === user.id} onOpenChange={(open) => { setOpenUnarchive(open); if (open) setArchiveId(user.id); }}>
                            <DialogTrigger asChild><Button variant="outline" size="icon"><RefreshCw size={16} /></Button></DialogTrigger>
                            <DialogContent>
                              <DialogHeader><DialogTitle className="text-lg font-medium">Unarchive Person</DialogTitle><DialogDescription className="text-sm">Unarchiving will make this person active again.</DialogDescription></DialogHeader>
                              <div className="py-4"><p className="text-sm">Are you sure you want to unarchive "{user.name}"?</p></div>
                              <DialogFooter><DialogClose asChild><Button variant="outline" className="text-sm">Cancel</Button></DialogClose><Button onClick={handleUnarchive} className="gap-2 text-sm" disabled={isLoading}>{isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={16} />}Unarchive Person</Button></DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </Can>
                      )}
                    </div>
                  </TableCell>

                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center h-24">
                    No people found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
              </PaginationItem>
              <PaginationItem>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </PaginationItem>
              {getPageNumbers().map((pageNum) => (
                <PaginationItem key={pageNum}>
                  <Button
                    variant={currentPage === pageNum ? "outline" : "ghost"}
                    onClick={() => handlePageChange(pageNum)}
                    className="size-9"
                  >
                    {pageNum}
                  </Button>
                </PaginationItem>
              ))}
              <PaginationItem>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </PaginationItem>
              <PaginationItem>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </TabsContent>

        <TabsContent value="qualifications" className="mt-0">
          {ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION) ? (
            <TrainingQualificationRegister />
          ) : (
            <div className="mt-8">
              <AccessRestricted 
                accessMessage="You do not have the required permissions to view the Training & Qualification Register."
                backMessage="Back to Directory"
                onBackClick={() => setActiveMainTab("directory")}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
